import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import type { PaginatedResponse } from "@/shared/http";

import type { FileRecord } from "../FileRecord";

import { fileQueryKeys } from "./fileQueryKeys";

export interface UseOrgFileSearchOptions {
  /**
   * The org whose files are searched.
   *
   * Required, and required to be right: the search route scopes to this and
   * returns an **empty page** — not an error — when it is missing or names an
   * org the caller cannot search. A picker that looks empty is therefore as
   * likely to be mis-scoped as it is to be genuinely empty.
   */
  orgId: string;
  /** Substring matched against the dataset-relative path. */
  searchTerm?: string;
  /** Extensions to restrict results to, with or without a leading dot. */
  extensions?: string[];
  pageSize?: number;
  enabled?: boolean;
}

/**
 * One flat, paginated list of an org's files, narrowed by extension and path.
 *
 * Deliberately **not** a suspense query. The three answers this can give —
 * "still asking", "asked, and nothing matched your term", "asked, and this org
 * has none of these files at all" — are different things to say to a user, and
 * a suspense boundary collapses the first into a fallback the caller cannot
 * distinguish from the other two. Callers render all three from `isPending`,
 * `data`, and the term they asked with.
 *
 * Results are not kept across a term change (no `placeholderData`), so an empty
 * `data` is never left over from a previous search: while a new term is in
 * flight the query reads as pending, which is the truthful state.
 */
export function useOrgFileSearch({
  orgId,
  searchTerm,
  extensions,
  pageSize = 50,
  enabled = true,
}: UseOrgFileSearchOptions): UseInfiniteQueryResult<FileRecord[], Error> {
  const { files } = useDomainServices();

  return useInfiniteQuery({
    queryKey: fileQueryKeys.search(orgId, { searchTerm, extensions, pageSize }),
    queryFn: ({ pageParam, signal }): Promise<PaginatedResponse<FileRecord>> =>
      files.searchFilesInOrg(orgId, {
        searchTerm,
        extensions,
        pageSize,
        nextToken: pageParam,
        options: { abortSignal: signal },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<FileRecord>) =>
      lastPage.next_token ?? undefined,
    select: (data): FileRecord[] => data.pages.flatMap((page) => page.items),
    enabled,
    // Short, like the other search-shaped queries: a picker's listing is a
    // snapshot of something a conversion is actively changing.
    gcTime: 60 * 1000,
  });
}
